import { useState, useEffect, useCallback, useRef } from 'react';
import { api, API } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Switch } from '../../components/ui/switch';
import { QRCodeSVG } from 'qrcode.react';
import { CalendarDays, Save, Loader2, Plus, Trash2, X, Printer, GripVertical, Eye, EyeOff, Send, RotateCcw, Sparkles, ChevronUp, ChevronDown, QrCode, ZoomIn, ZoomOut, Mail, FileText, Clock, Check, Copy, Camera, Globe, History } from 'lucide-react';
import { toast } from 'sonner';
import ScanMenuModal from '../../components/ScanMenuModal';

const QUICK_ACTIONS = [
  { label: 'Idees entrees', prompt: 'Propose 3 idees d\'entrees du jour avec des produits de saison locaux' },
  { label: 'Plats du jour', prompt: 'Propose 3 plats du jour originaux pour cette semaine' },
  { label: 'Desserts saison', prompt: 'Propose 2 desserts de saison simples et gourmands' },
  { label: 'Embellir noms', prompt: 'Prends les plats actuels du menu et propose des intitules plus attractifs et gourmands' },
  { label: 'Menu complet', prompt: 'Propose un menu du jour complet (2 entrees, 3 plats, 2 desserts) avec des produits de saison' },
];

const MenuDuJour = () => {
  const [menu, setMenu] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [showPreview, setShowPreview] = useState(true);
  const [showAI, setShowAI] = useState(false);
  const [printZoom, setPrintZoom] = useState(100);
  const [showQrJeu, setShowQrJeu] = useState(false);
  const [showQrCarte, setShowQrCarte] = useState(true);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [showProgrammer, setShowProgrammer] = useState(false);
  const [showScanModal, setShowScanModal] = useState(false);
  const [showSentHistory, setShowSentHistory] = useState(false);

  // AI chat state
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const [sessionId] = useState(() => crypto.randomUUID());
  const chatEndRef = useRef(null);
  const inputRef = useRef(null);

  const loadMenu = useCallback(async () => {
    try {
      const res = await api.get('/menu-du-jour');
      setMenu(res.data);
    } catch { setMenu(null); }
    setLoading(false);
  }, []);

  useEffect(() => { loadMenu(); }, [loadMenu]);

  useEffect(() => { chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [chatMessages]);

  const save = async () => {
    setSaving(true);
    try {
      await api.put('/menu-du-jour', menu);
      setDirty(false);
      toast.success('Menu du jour sauvegarde');
    } catch { toast.error('Erreur'); }
    setSaving(false);
  };

  const update = (field, value) => { setMenu(prev => ({ ...prev, [field]: value })); setDirty(true); };

  const updateItem = (section, idx, field, value) => {
    setMenu(prev => ({
      ...prev,
      [section]: prev[section].map((item, i) => i === idx ? { ...item, [field]: value } : item)
    }));
    setDirty(true);
  };
  const addItem = (section, template) => {
    setMenu(prev => ({ ...prev, [section]: [...prev[section], { id: crypto.randomUUID(), ...template }] }));
    setDirty(true);
  };
  const removeItem = (section, idx) => {
    setMenu(prev => ({ ...prev, [section]: prev[section].filter((_, i) => i !== idx) }));
    setDirty(true);
  };
  const moveItem = (section, idx, direction) => {
    setMenu(prev => {
      const arr = [...prev[section]];
      const newIdx = idx + direction;
      if (newIdx < 0 || newIdx >= arr.length) return prev;
      [arr[idx], arr[newIdx]] = [arr[newIdx], arr[idx]];
      return { ...prev, [section]: arr };
    });
    setDirty(true);
  };

  const printMenu = () => {
    const el = document.getElementById('menu-du-jour-print');
    if (!el) return;
    const scale = printZoom / 100;
    
    // Generate QR code as inline SVG for reliable print
    const qrContainer = document.createElement('div');
    const tempRoot = document.createElement('div');
    document.body.appendChild(tempRoot);
    
    // Get the QR SVG from the preview if it exists, or create a simple text fallback
    const existingSvg = el.querySelector('svg');
    let qrSvgHtml = '';
    if (showQrCarte) {
      // Create a canvas-based QR code for print
      const qrCanvas = document.createElement('canvas');
      const qrSvgEl = document.querySelector('#menu-du-jour-print svg');
      if (qrSvgEl) {
        qrSvgHtml = `<div style="text-align:center;margin-top:20px;padding-top:15px;border-top:1px solid #eee;">
          <img src="${API}/api/site/qrcode/menu?size=200&t=${Date.now()}" 
               onerror="this.style.display='none'" 
               style="width:70px;height:70px;display:inline-block;" />
          <p style="font-family:Manrope,Arial,sans-serif;font-size:8px;color:#999;margin-top:4px;letter-spacing:1px;">SCANNEZ POUR VOIR NOTRE CARTE EN LIGNE</p>
        </div>`;
      }
    }
    
    const w = window.open('', '_blank');
    w.document.write(`<html><head><title>Menu du Jour - Le Mas</title>
    <link href="https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@300;400;500;600&family=Manrope:wght@300;400;500;600&display=swap" rel="stylesheet">
    <style>
      @page { size: A4 portrait; margin: 15mm; }
      * { box-sizing: border-box; margin: 0; }
      body { margin:0; padding:0; font-family:'Cormorant Garamond',serif; }
      .print-wrapper { 
        width: 180mm;
        max-width: 180mm;
        margin: 0 auto;
        font-size: ${scale * 100}%;
      }
      @media print { 
        body { padding: 0; }
        .no-print { display: none !important; }
      }
    </style></head><body>
    <div class="print-wrapper">${el.innerHTML}</div>
    ${qrSvgHtml}
    <script>
      // Wait for images to load before printing
      var imgs = document.querySelectorAll('img');
      var loaded = 0;
      var total = imgs.length;
      if (total === 0) { setTimeout(function(){window.print()}, 400); }
      else {
        imgs.forEach(function(img) {
          if (img.complete) { loaded++; if(loaded>=total) setTimeout(function(){window.print()},400); }
          else {
            img.onload = function() { loaded++; if(loaded>=total) setTimeout(function(){window.print()},400); };
            img.onerror = function() { loaded++; if(loaded>=total) setTimeout(function(){window.print()},400); };
          }
        });
        setTimeout(function(){window.print()}, 3000); // fallback
      }
    </script></body></html>`);
  };

  // AI Chat
  const sendMessage = async (text) => {
    const msg = text || chatInput.trim();
    if (!msg) return;
    setChatInput('');
    setChatMessages(prev => [...prev, { role: 'user', text: msg }]);
    setChatLoading(true);
    try {
      const res = await api.post('/menu-du-jour/ai-chat', { message: msg, session_id: sessionId });
      setChatMessages(prev => [...prev, {
        role: 'assistant',
        text: res.data.response,
        suggestions: res.data.suggestions,
      }]);
    } catch {
      setChatMessages(prev => [...prev, { role: 'assistant', text: 'Erreur de connexion avec l\'IA.' }]);
    }
    setChatLoading(false);
    inputRef.current?.focus();
  };

  const resetChat = async () => {
    setChatMessages([]);
    try { await api.post('/menu-du-jour/ai-chat/reset', { session_id: sessionId }); } catch {}
  };

  const applySuggestion = (suggestion) => {
    const section = suggestion.section || 'plats';
    const newItem = {
      id: crypto.randomUUID(),
      nom: suggestion.nom,
    };
    if (section === 'plats') {
      newItem.accompagnement = suggestion.accompagnement || suggestion.description || '';
    } else {
      newItem.description = suggestion.description || '';
    }
    setMenu(prev => ({ ...prev, [section]: [...(prev[section] || []), newItem] }));
    setDirty(true);
    toast.success(`"${suggestion.nom}" ajoute aux ${section}`);
  };

  if (loading || !menu) return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-[rgb(var(--lm-gold))]" /></div>;

  return (
    <div className="h-full flex flex-col" data-testid="menu-du-jour-page">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-2 mb-3 shrink-0">
        <div className="flex items-center gap-3">
          <CalendarDays size={18} className="text-[rgb(var(--lm-gold))]" />
          <h1 className="text-lg font-title text-[rgb(var(--lm-text))]">Menu du Jour</h1>
          <span className="px-2 py-0.5 rounded-full bg-[rgb(var(--lm-gold)/0.1)] text-[rgb(var(--lm-gold))] text-[9px] font-medium ml-2">Visible Lun-Ven midi</span>
          <span className="px-2 py-0.5 rounded-full bg-[rgb(var(--lm-surface))] text-[rgb(var(--lm-text-muted))] text-[9px]">Week-end : formules seules</span>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setShowAI(!showAI)}
            className={`flex items-center gap-1 text-xs transition-colors px-2 py-1 rounded-lg ${showAI ? 'bg-[rgb(var(--lm-gold)/0.15)] text-[rgb(var(--lm-gold))]' : 'text-[rgb(var(--lm-text-muted))] hover:text-[rgb(var(--lm-text))]'}`}
            data-testid="toggle-ai">
            <Sparkles size={13} />
            IA
          </button>
          <button onClick={() => setShowPreview(!showPreview)}
            className="flex items-center gap-1 text-xs text-[rgb(var(--lm-text-muted))] hover:text-[rgb(var(--lm-text))] transition-colors"
            data-testid="toggle-preview">
            {showPreview ? <EyeOff size={13} /> : <Eye size={13} />}
            {showPreview ? 'Masquer' : 'Apercu'}
          </button>
          <Button size="sm" variant="ghost" onClick={() => window.open(`${API}/api/menu-du-jour/pdf`, '_blank')}
            className="text-[rgb(var(--lm-text-muted))] text-xs h-8" data-testid="print-menu">
            <FileText size={14} className="mr-1" /> PDF
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setShowEmailModal(true)}
            className="text-[rgb(var(--lm-text-muted))] text-xs h-8" data-testid="email-menu">
            <Mail size={14} className="mr-1" /> Email
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setShowSentHistory(true)}
            className="text-[rgb(var(--lm-text-muted))] text-xs h-8" data-testid="menu-sent-history-btn">
            <History size={14} className="mr-1" /> Envois & Ouvertures
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setShowProgrammer(true)}
            className="text-[rgb(var(--lm-text-muted))] text-xs h-8" data-testid="programmer-menu">
            <Clock size={14} className="mr-1" /> Programmer
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setShowScanModal(true)}
            className="text-[rgb(var(--lm-text-muted))] text-xs h-8" data-testid="scan-menu-btn">
            <Camera size={14} className="mr-1" /> Scan
          </Button>
          <Button size="sm" onClick={async () => {
            try {
              // Save first if dirty
              if (dirty) await save();
              const r = await api.post('/menu-du-jour/publish');
              toast.success(r.data.message || 'Menu en ligne !');
            } catch { toast.error('Erreur publication'); }
          }}
            className="bg-green-600 hover:bg-green-700 text-white text-xs h-8" data-testid="publish-menu-btn">
            <Globe size={14} className="mr-1" /> Mettre en ligne
          </Button>
          {dirty && (
            <Button size="sm" onClick={save} disabled={saving}
              className="bg-[rgb(var(--lm-gold))] text-[rgb(var(--lm-bg))] hover:opacity-90 text-xs h-8" data-testid="save-menu">
              {saving ? <Loader2 size={14} className="animate-spin mr-1" /> : <Save size={14} className="mr-1" />}
              Sauvegarder
            </Button>
          )}
        </div>
      </div>

      {/* Print controls strip */}
      <div className="flex items-center gap-3 mb-3 px-2 py-1.5 rounded-lg bg-[rgb(var(--lm-card))] shrink-0">
        <div className="flex items-center gap-2">
          <ZoomOut size={12} className="text-[rgb(var(--lm-text-muted))]" />
          <input type="range" min="60" max="150" value={printZoom} onChange={e => setPrintZoom(Number(e.target.value))}
            className="w-24 h-1 accent-[rgb(var(--lm-gold))]" data-testid="zoom-slider" />
          <ZoomIn size={12} className="text-[rgb(var(--lm-text-muted))]" />
          <span className="text-[9px] text-[rgb(var(--lm-text-muted))] w-8">{printZoom}%</span>
        </div>
        <div className="w-px h-4 bg-[rgb(var(--lm-border))]" />
        <label className="flex items-center gap-1.5 cursor-pointer" data-testid="qr-jeu-toggle">
          <QrCode size={12} className={showQrJeu ? 'text-[rgb(var(--lm-gold))]' : 'text-[rgb(var(--lm-text-muted))]'} />
          <span className="text-[9px] text-[rgb(var(--lm-text-muted))]">Jeu</span>
          <Switch checked={showQrJeu} onCheckedChange={setShowQrJeu} className="h-4 w-7" />
        </label>
        <label className="flex items-center gap-1.5 cursor-pointer" data-testid="qr-carte-toggle">
          <QrCode size={12} className={showQrCarte ? 'text-[rgb(var(--lm-gold))]' : 'text-[rgb(var(--lm-text-muted))]'} />
          <span className="text-[9px] text-[rgb(var(--lm-text-muted))]">Carte</span>
          <Switch checked={showQrCarte} onCheckedChange={setShowQrCarte} className="h-4 w-7" />
        </label>
      </div>

      {/* ── Menu Editor ── */}
      <div className={`flex-1 grid gap-3 min-h-0 ${
        showAI && showPreview ? 'grid-cols-1 lg:grid-cols-[1fr_320px_360px]' :
        showAI ? 'grid-cols-1 lg:grid-cols-[1fr_320px]' :
        showPreview ? 'grid-cols-1 lg:grid-cols-[1fr_380px]' :
        'grid-cols-1 max-w-2xl'
      }`}>
        {/* LEFT: Editor */}
        <div className="space-y-3 overflow-y-auto min-h-0">
          {/* Title & Dates */}
          <div className="lm-card p-4 space-y-3">
            <h2 className="text-[10px] tracking-[0.2em] uppercase text-[rgb(var(--lm-text-muted))]">Titre & Dates</h2>
            <Input value={menu.titre || ''} onChange={e => update('titre', e.target.value)} placeholder="Menu de la Semaine"
              className="bg-[rgb(var(--lm-surface))] border-[rgb(var(--lm-border))] text-sm" data-testid="menu-titre" />
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-[9px] text-[rgb(var(--lm-text-muted))] uppercase">Du</label>
                <Input type="date" value={menu.date_debut || ''} onChange={e => update('date_debut', e.target.value)}
                  className="bg-[rgb(var(--lm-surface))] border-[rgb(var(--lm-border))] text-xs" data-testid="date-debut" />
              </div>
              <div>
                <label className="text-[9px] text-[rgb(var(--lm-text-muted))] uppercase">Au</label>
                <Input type="date" value={menu.date_fin || ''} onChange={e => update('date_fin', e.target.value)}
                  className="bg-[rgb(var(--lm-surface))] border-[rgb(var(--lm-border))] text-xs" data-testid="date-fin" />
              </div>
            </div>
            <div className="flex items-center justify-between pt-1">
              <div>
                <p className="text-xs text-[rgb(var(--lm-text))]">QR code Roue du Mas</p>
                <p className="text-[10px] text-[rgb(var(--lm-text-muted))]">Afficher sur le menu imprime</p>
              </div>
              <Switch checked={menu.show_qr_roue === true} onCheckedChange={v => update('show_qr_roue', v)} data-testid="toggle-qr" />
            </div>
          </div>

          {/* Entrees */}
          <SectionEditor title="Entrees" items={menu.entrees || []}
            onUpdate={(idx, f, v) => updateItem('entrees', idx, f, v)}
            onAdd={() => addItem('entrees', { nom: '', description: '' })}
            onRemove={idx => removeItem('entrees', idx)}
            onMove={(idx, dir) => moveItem('entrees', idx, dir)}
            fields={['nom', 'description']}
            placeholders={['Nom du plat', 'Description / garniture']}
            testId="entrees" />

          {/* Plats */}
          <SectionEditor title="Plats" items={menu.plats || []}
            onUpdate={(idx, f, v) => updateItem('plats', idx, f, v)}
            onAdd={() => addItem('plats', { nom: '', accompagnement: '' })}
            onRemove={idx => removeItem('plats', idx)}
            onMove={(idx, dir) => moveItem('plats', idx, dir)}
            fields={['nom', 'accompagnement']}
            placeholders={['Nom du plat', 'Accompagnement']}
            testId="plats" />

          {/* Desserts */}
          <SectionEditor title="Desserts" items={menu.desserts || []}
            onUpdate={(idx, f, v) => updateItem('desserts', idx, f, v)}
            onAdd={() => addItem('desserts', { nom: '', description: '' })}
            onRemove={idx => removeItem('desserts', idx)}
            onMove={(idx, dir) => moveItem('desserts', idx, dir)}
            fields={['nom', 'description']}
            placeholders={['Nom du dessert', 'Description']}
            testId="desserts" />

          {/* Formules */}
          <div className="lm-card p-4">
            <div className="flex items-center justify-between mb-2">
              <h2 className="text-[10px] tracking-[0.2em] uppercase text-[rgb(var(--lm-text-muted))]">Formules</h2>
            </div>
            <div className="space-y-1.5">
              {(menu.formules || []).map((f, idx) => (
                <div key={f.id || idx} className="flex items-center gap-2 group" data-testid={`formule-${idx}`}>
                  <div className="flex flex-col gap-0.5 shrink-0">
                    <button onClick={() => moveItem('formules', idx, -1)} disabled={idx === 0}
                      className={`w-5 h-5 flex items-center justify-center rounded border transition-colors ${idx === 0 ? 'border-transparent text-[rgb(var(--lm-text-muted)/0.2)] cursor-default' : 'border-[rgb(var(--lm-border))] text-[rgb(var(--lm-text-muted))] hover:bg-[rgb(var(--lm-gold)/0.15)] hover:text-[rgb(var(--lm-gold))]'}`}>
                      <ChevronUp size={12} />
                    </button>
                    <button onClick={() => moveItem('formules', idx, 1)} disabled={idx === (menu.formules || []).length - 1}
                      className={`w-5 h-5 flex items-center justify-center rounded border transition-colors ${idx === (menu.formules || []).length - 1 ? 'border-transparent text-[rgb(var(--lm-text-muted)/0.2)] cursor-default' : 'border-[rgb(var(--lm-border))] text-[rgb(var(--lm-text-muted))] hover:bg-[rgb(var(--lm-gold)/0.15)] hover:text-[rgb(var(--lm-gold))]'}`}>
                      <ChevronDown size={12} />
                    </button>
                  </div>
                  <Input value={f.label} onChange={e => updateItem('formules', idx, 'label', e.target.value)}
                    className="h-7 text-xs flex-1 bg-[rgb(var(--lm-surface))] border-[rgb(var(--lm-border))]" placeholder="Ex: Entree + Plat + Dessert" />
                  <Input type="number" step="0.5" value={f.prix} onChange={e => updateItem('formules', idx, 'prix', parseFloat(e.target.value) || 0)}
                    className="h-7 w-20 text-xs font-mono text-right bg-[rgb(var(--lm-surface))] border-[rgb(var(--lm-border))]" data-testid={`formule-prix-${idx}`} />
                  <span className="text-[10px] text-[rgb(var(--lm-text-muted))]">EUR</span>
                  <button onClick={() => removeItem('formules', idx)}
                    className="opacity-0 group-hover:opacity-100 text-[rgb(var(--lm-red)/0.4)] hover:text-[rgb(var(--lm-red))] transition-opacity">
                    <Trash2 size={12} />
                  </button>
                </div>
              ))}
              <button onClick={() => addItem('formules', { label: '', prix: 0 })}
                className="flex items-center gap-1 text-[10px] text-[rgb(var(--lm-text-muted))] hover:text-[rgb(var(--lm-gold))] pt-1 transition-colors" data-testid="add-formule">
                <Plus size={10} /> Ajouter une formule
              </button>
            </div>
          </div>
        </div>

        {/* CENTER: AI Panel */}
        {showAI && (
          <div className="flex flex-col rounded-xl overflow-hidden border border-[rgb(var(--lm-border))] bg-[rgb(var(--lm-surface))] min-h-0 lg:min-h-[500px]" data-testid="ai-panel-mdj">
            <div className="px-3 py-2.5 flex items-center gap-2 border-b border-[rgb(var(--lm-border))] shrink-0">
              <div className="w-2 h-2 rounded-full bg-amber-400 animate-pulse" />
              <h3 className="text-xs font-medium text-[rgb(var(--lm-text))]">Assistant Menu</h3>
              <div className="flex-1" />
              <button onClick={resetChat} className="text-[rgb(var(--lm-text-muted))] hover:text-[rgb(var(--lm-text))] transition-colors" data-testid="reset-ai-chat">
                <RotateCcw size={12} />
              </button>
            </div>
            <div className="px-2 py-1.5 flex gap-1 flex-wrap border-b border-[rgb(var(--lm-border))] shrink-0">
              {QUICK_ACTIONS.map(qa => (
                <button key={qa.label} onClick={() => sendMessage(qa.prompt)}
                  className="text-[9px] px-2 py-0.5 rounded-full bg-[rgb(var(--lm-surface-elevated))] text-[rgb(var(--lm-text-muted))] hover:bg-[rgb(var(--lm-gold)/0.15)] hover:text-[rgb(var(--lm-gold))] transition-all border border-[rgb(var(--lm-border))]"
                  data-testid={`mdj-qa-${qa.label.replace(/\s/g, '-')}`}>
                  {qa.label}
                </button>
              ))}
            </div>
            <div className="flex-1 overflow-y-auto px-3 py-2 min-h-0">
              {chatMessages.length === 0 && (
                <div className="text-center py-8">
                  <Sparkles size={24} className="mx-auto text-[rgb(var(--lm-gold)/0.25)] mb-2" />
                  <p className="text-[10px] text-[rgb(var(--lm-text-muted))]">Idees de plats, embellir les noms...</p>
                </div>
              )}
              {chatMessages.map((msg, i) => (
                <AIChatMessage key={i} msg={msg} onApply={applySuggestion} />
              ))}
              {chatLoading && (
                <div className="flex items-center gap-2 text-[rgb(var(--lm-text-muted))] text-xs mb-2">
                  <Loader2 size={12} className="animate-spin" /> Reflexion...
                </div>
              )}
              <div ref={chatEndRef} />
            </div>
            <div className="px-2 py-2 border-t border-[rgb(var(--lm-border))] shrink-0">
              <div className="flex gap-1.5">
                <Input ref={inputRef} value={chatInput} onChange={e => setChatInput(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); } }}
                  placeholder="Ex: Embellir mes intitules..."
                  className="bg-[rgb(var(--lm-surface-elevated))] border-[rgb(var(--lm-border))] text-[rgb(var(--lm-text))] placeholder:text-[rgb(var(--lm-text-muted))] text-xs h-8"
                  disabled={chatLoading} data-testid="mdj-chat-input" />
                <Button onClick={() => sendMessage()} disabled={chatLoading || !chatInput.trim()} size="sm"
                  className="bg-[rgb(var(--lm-gold))] text-[rgb(var(--lm-bg))] hover:opacity-90 border-0 px-2 h-8" data-testid="mdj-chat-send">
                  <Send size={12} />
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* RIGHT: Preview */}
        {showPreview && (
          <div className="lg:sticky lg:top-4 self-start overflow-y-auto max-h-[calc(100vh-120px)]">
            <MenuPreview menu={menu} showQrJeu={showQrJeu} showQrCarte={showQrCarte} />
          </div>
        )}
      </div>

      {/* Email Modal */}
      {showEmailModal && <EmailMenuModal menu={menu} onClose={() => setShowEmailModal(false)} />}
      {showSentHistory && <MenuSentHistoryModal onClose={() => setShowSentHistory(false)} />}

      {/* Programmer Modal */}
      {showProgrammer && <ProgrammerModal currentMenu={menu} onClose={() => setShowProgrammer(false)} />}

      {/* Scan Menu Modal */}
      {showScanModal && <ScanMenuModal onClose={() => setShowScanModal(false)} onImport={(plats) => {
        // Group by categorie and add to menu
        const entrees = plats.filter(p => p.categorie === 'entree').map(p => ({ nom: p.nom, description: p.description }));
        const platsArr = plats.filter(p => ['plat', 'viande', 'poisson', 'suggestion'].includes(p.categorie)).map(p => ({ nom: p.nom, description: p.description }));
        const desserts = plats.filter(p => p.categorie === 'dessert').map(p => ({ nom: p.nom, description: p.description }));
        const tapas = plats.filter(p => p.categorie === 'tapas').map(p => ({ nom: p.nom, description: p.description }));
        setMenu(prev => ({
          ...prev,
          entrees: [...(prev.entrees || []), ...entrees],
          plats: [...(prev.plats || []), ...platsArr, ...tapas],
          desserts: [...(prev.desserts || []), ...desserts],
        }));
        setDirty(true);
        setShowScanModal(false);
        toast.success(`${plats.length} plats importes !`);
      }} />}
    </div>
  );
};

/* ── AI Chat Message ── */
const AIChatMessage = ({ msg, onApply }) => {
  if (msg.role === 'user') {
    return (
      <div className="flex justify-end mb-2">
        <div className="bg-[rgb(var(--lm-gold)/0.15)] text-[rgb(var(--lm-text))] text-xs px-3 py-2 rounded-xl rounded-br-sm max-w-[90%]">
          {msg.text}
        </div>
      </div>
    );
  }
  return (
    <div className="mb-3">
      {msg.text && (
        <div className="bg-[rgb(var(--lm-surface-elevated))] text-[rgb(var(--lm-text-secondary))] text-xs px-3 py-2.5 rounded-xl rounded-bl-sm max-w-[95%] leading-relaxed whitespace-pre-wrap">
          {msg.text}
        </div>
      )}
      {msg.suggestions?.length > 0 && (
        <div className="mt-1.5 space-y-1">
          {msg.suggestions.map((s, i) => (
            <div key={i} className="flex items-center gap-1.5 bg-[rgb(var(--lm-bg))] border border-[rgb(var(--lm-gold)/0.2)] rounded-lg px-2 py-2" data-testid={`mdj-suggestion-${i}`}>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-medium text-[rgb(var(--lm-text))]">{s.nom}</p>
                {(s.description || s.accompagnement) && (
                  <p className="text-[10px] text-[rgb(var(--lm-text-muted))] truncate">{s.description || s.accompagnement}</p>
                )}
              </div>
              <button onClick={() => onApply(s)}
                className="shrink-0 text-[10px] bg-[rgb(var(--lm-gold))] text-[rgb(var(--lm-bg))] px-2 py-0.5 rounded-md hover:opacity-90 transition-opacity font-medium"
                data-testid={`mdj-apply-${i}`}>
                + Ajouter
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

/* ── Section Editor ── */
const SectionEditor = ({ title, items, onUpdate, onAdd, onRemove, onMove, fields, placeholders, testId }) => (
  <div className="lm-card p-4">
    <div className="flex items-center justify-between mb-2">
      <h2 className="text-[10px] tracking-[0.2em] uppercase text-[rgb(var(--lm-text-muted))]">{title}</h2>
      <span className="text-[10px] text-[rgb(var(--lm-text-muted))] font-mono">{items.length}</span>
    </div>
    <div className="space-y-2">
      {items.map((item, idx) => (
        <div key={item.id || idx} className="flex items-start gap-2 group" data-testid={`${testId}-${idx}`}>
          <div className="flex flex-col gap-0.5 shrink-0 mt-1">
            <button onClick={() => onMove(idx, -1)} disabled={idx === 0}
              className={`w-5 h-5 flex items-center justify-center rounded border transition-colors ${idx === 0 ? 'border-transparent text-[rgb(var(--lm-text-muted)/0.2)] cursor-default' : 'border-[rgb(var(--lm-border))] text-[rgb(var(--lm-text-muted))] hover:bg-[rgb(var(--lm-gold)/0.15)] hover:text-[rgb(var(--lm-gold))] hover:border-[rgb(var(--lm-gold)/0.3)]'}`}
              data-testid={`${testId}-${idx}-up`}>
              <ChevronUp size={14} />
            </button>
            <button onClick={() => onMove(idx, 1)} disabled={idx === items.length - 1}
              className={`w-5 h-5 flex items-center justify-center rounded border transition-colors ${idx === items.length - 1 ? 'border-transparent text-[rgb(var(--lm-text-muted)/0.2)] cursor-default' : 'border-[rgb(var(--lm-border))] text-[rgb(var(--lm-text-muted))] hover:bg-[rgb(var(--lm-gold)/0.15)] hover:text-[rgb(var(--lm-gold))] hover:border-[rgb(var(--lm-gold)/0.3)]'}`}
              data-testid={`${testId}-${idx}-down`}>
              <ChevronDown size={14} />
            </button>
          </div>
          <div className="flex-1 space-y-1">
            {fields.map((field, fi) => (
              <Input key={field} value={item[field] || ''} onChange={e => onUpdate(idx, field, e.target.value)}
                placeholder={placeholders[fi]} data-testid={`${testId}-${idx}-${field}`}
                className={`h-7 text-xs bg-[rgb(var(--lm-surface))] border-[rgb(var(--lm-border))] ${fi > 0 ? 'text-[rgb(var(--lm-text-muted))] italic' : ''}`} />
            ))}
          </div>
          <button onClick={() => onRemove(idx)}
            className="text-[rgb(var(--lm-red)/0.4)] hover:text-[rgb(var(--lm-red))] transition-colors mt-1.5 shrink-0 p-1">
            <Trash2 size={14} />
          </button>
        </div>
      ))}
      <button onClick={onAdd}
        className="flex items-center gap-1 text-[10px] text-[rgb(var(--lm-text-muted))] hover:text-[rgb(var(--lm-gold))] pt-1 transition-colors" data-testid={`add-${testId}`}>
        <Plus size={10} /> Ajouter
      </button>
    </div>
  </div>
);

/* ── Print Preview ── */

const QRRoueHebdo = () => {
  const [token, setToken] = useState('');
  useEffect(() => {
    fetch(`${API}/api/roue/weekly-token`).then(r => r.json()).then(d => setToken(d.token)).catch(() => {});
  }, []);
  if (!token) return null;
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '16px' }}>
      <QRCodeSVG value={`${window.location.origin}/roue?t=${token}`} size={56} bgColor="#FFFFFF" fgColor="#1B2A52" level="L" />
      <div style={{ textAlign: 'left' }}>
        <p style={{ fontFamily: "'Arial', sans-serif", fontWeight: 'bold', fontSize: '10px', letterSpacing: '0.15em', textTransform: 'uppercase', color: '#1B2A52' }}>
          Tentez votre chance !
        </p>
        <p style={{ fontStyle: 'italic', fontSize: '9px', color: '#666', marginTop: '2px' }}>
          Scannez et tournez la roue<br />pour gagner un cadeau
        </p>
      </div>
    </div>
  );
};

const MenuPreview = ({ menu, showQrJeu, showQrCarte }) => {
  const formatDateRange = () => {
    if (!menu.date_debut || !menu.date_fin) return '';
    const opts = { weekday: 'long', day: 'numeric', month: 'long' };
    const d1 = new Date(menu.date_debut + 'T00:00:00');
    const d2 = new Date(menu.date_fin + 'T00:00:00');
    const s1 = d1.toLocaleDateString('fr-FR', opts);
    const s2 = d2.toLocaleDateString('fr-FR', opts);
    return `Du ${s1} au ${s2}`;
  };

  const diamond = <p style={{ textAlign: 'center', color: '#1B2A52', letterSpacing: '0.15em', margin: '16px 0', fontSize: '12px' }}>&mdash; &#9830; &mdash;</p>;

  return (
    <div id="menu-du-jour-print" style={{
      background: 'white', color: '#1B2A52', padding: '40px 36px', maxWidth: '420px', margin: '0 auto',
      fontFamily: "'Georgia', serif", boxShadow: '0 4px 24px rgba(0,0,0,0.12)'
    }} data-testid="menu-preview">
      <div style={{ textAlign: 'center', marginBottom: '16px' }}>
        <img src="/logo-carte.jpg" alt="Le Mas" style={{ height: '80px', objectFit: 'contain', display: 'inline-block' }} />
      </div>
      <p style={{ textAlign: 'center', fontStyle: 'italic', fontSize: '10px', color: '#666', marginBottom: '12px' }}>
        Saint-Laurent-de-la-Salanque &middot; Pays Catalan
      </p>
      <h1 style={{
        textAlign: 'center', fontFamily: "'Arial', sans-serif", fontWeight: 'bold',
        fontSize: '18px', letterSpacing: '0.35em', textTransform: 'uppercase',
        color: '#1B2A52', marginBottom: '4px'
      }}>
        {menu.titre || 'Menu de la Semaine'}
      </h1>
      {formatDateRange() && (
        <p style={{ textAlign: 'center', fontStyle: 'italic', fontSize: '11px', color: '#666', marginBottom: '0' }}>
          {formatDateRange()}
        </p>
      )}
      {diamond}
      {(menu.entrees || []).length > 0 && (
        <div style={{ marginBottom: '20px' }}>
          <h2 style={{ textAlign: 'center', fontFamily: "'Arial', sans-serif", fontWeight: 'bold', fontSize: '14px', letterSpacing: '0.3em', textTransform: 'uppercase', color: '#1B2A52', marginBottom: '10px' }}>ENTREES</h2>
          {menu.entrees.map((e, i) => (
            <div key={e.id || i} style={{ textAlign: 'center', marginBottom: '8px' }}>
              <p style={{ fontWeight: 'bold', fontSize: '12px', color: '#1B2A52' }}>{e.nom}</p>
              {e.description && <p style={{ fontStyle: 'italic', fontSize: '13px', color: '#666', marginTop: '2px' }}>{e.description}</p>}
            </div>
          ))}
        </div>
      )}
      {(menu.plats || []).length > 0 && (
        <div style={{ marginBottom: '20px' }}>
          <h2 style={{ textAlign: 'center', fontFamily: "'Arial', sans-serif", fontWeight: 'bold', fontSize: '14px', letterSpacing: '0.3em', textTransform: 'uppercase', color: '#1B2A52', marginBottom: '10px' }}>PLATS</h2>
          {menu.plats.map((p, i) => (
            <div key={p.id || i} style={{ textAlign: 'center', marginBottom: '8px' }}>
              <p style={{ fontWeight: 'bold', fontSize: '12px', color: '#1B2A52' }}>{p.nom}</p>
              {p.accompagnement && <p style={{ fontStyle: 'italic', fontSize: '13px', color: '#666', marginTop: '2px' }}>{p.accompagnement}</p>}
            </div>
          ))}
        </div>
      )}
      {(menu.desserts || []).length > 0 && (
        <div style={{ marginBottom: '6px' }}>
          <h2 style={{ textAlign: 'center', fontFamily: "'Arial', sans-serif", fontWeight: 'bold', fontSize: '14px', letterSpacing: '0.3em', textTransform: 'uppercase', color: '#1B2A52', marginBottom: '10px' }}>DESSERTS</h2>
          {menu.desserts.map((d, i) => (
            <div key={d.id || i} style={{ textAlign: 'center', marginBottom: '6px' }}>
              <p style={{ fontWeight: 'bold', fontSize: '12px', color: '#1B2A52' }}>{d.nom}</p>
              {d.description && <p style={{ fontStyle: 'italic', fontSize: '13px', color: '#666', marginTop: '2px' }}>{d.description}</p>}
            </div>
          ))}
        </div>
      )}
      {diamond}
      {(menu.formules || []).length > 0 && (
        <div style={{ marginBottom: '6px' }}>
          <h2 style={{ textAlign: 'center', fontFamily: "'Arial', sans-serif", fontWeight: 'bold', fontSize: '14px', letterSpacing: '0.3em', textTransform: 'uppercase', color: '#1B2A52', marginBottom: '12px' }}>NOS FORMULES</h2>
          <div style={{ maxWidth: '320px', margin: '0 auto' }}>
            {menu.formules.map((f, i) => (
              <div key={f.id || i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '6px', padding: '0 8px' }}>
                <span style={{ fontWeight: 'bold', fontSize: '12px', color: '#1B2A52' }}>{f.label}</span>
                <span style={{ borderBottom: '1px dotted #ccc', flex: 1, margin: '0 8px', minWidth: '20px' }} />
                <span style={{ fontWeight: 'bold', fontSize: '13px', color: '#1B2A52', whiteSpace: 'nowrap' }}>{f.prix} &euro;</span>
              </div>
            ))}
          </div>
        </div>
      )}
      {(menu.show_qr_roue || showQrJeu) && (
        <>
          {diamond}
          <QRRoueHebdo />
        </>
      )}
      {showQrCarte && (
        <>
          {diamond}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '16px' }}>
            <QRCodeSVG value={window.location.origin + '/site/restaurant/menu'} size={56} bgColor="#FFFFFF" fgColor="#1B2A52" level="L" />
            <div style={{ textAlign: 'left' }}>
              <p style={{ fontFamily: "'Arial', sans-serif", fontWeight: 'bold', fontSize: '10px', letterSpacing: '0.15em', textTransform: 'uppercase', color: '#1B2A52' }}>
                Carte multilingue
              </p>
              <p style={{ fontStyle: 'italic', fontSize: '9px', color: '#666', marginTop: '2px' }}>
                Scannez pour la carte<br />FR &middot; EN &middot; ES &middot; CA
              </p>
            </div>
          </div>
        </>
      )}
      {diamond}
      <div style={{ textAlign: 'center' }}>
        <p style={{ fontSize: '10px', color: '#666', fontStyle: 'italic' }}>www.restaurant-le-mas.fr</p>
        <p style={{ fontSize: '10px', color: '#666', fontStyle: 'italic' }}>04 68 38 06 98</p>
      </div>
    </div>
  );
};


/* ── Email Menu Modal ── */
const EmailMenuModal = ({ menu, onClose }) => {
  const [to, setTo] = useState('');
  const [message, setMessage] = useState('Bonjour,\n\nVoici notre menu du jour, bonne degustation !\n\nLe Mas Restaurant Traiteur');
  const [iaInstruction, setIaInstruction] = useState('');
  const [sending, setSending] = useState(false);
  const [generatingIA, setGeneratingIA] = useState(false);

  const generateMessage = async () => {
    setGeneratingIA(true);
    try {
      const res = await api.post('/menu-du-jour/generate-message', { instruction: iaInstruction });
      if (res.data.message) {
        setMessage(res.data.message);
        toast.success('Message genere par l\'IA');
      } else {
        toast.error(res.data.error || 'Erreur IA');
      }
    } catch { toast.error('Erreur IA'); }
    setGeneratingIA(false);
  };

  const sendEmail = async () => {
    if (!to.trim()) { toast.error('Email requis'); return; }
    setSending(true);
    try {
      const res = await api.post('/menu-du-jour/send-email', {
        to: to.trim(),
        subject: `Menu du Jour - ${menu.titre || 'Le Mas'}`,
        message: message,
      });
      if (res.data.success) {
        toast.success(`Email envoye a ${res.data.recipients} destinataire(s) !`);
        onClose();
      } else {
        toast.error(res.data.error || 'Erreur envoi');
      }
    } catch { toast.error('Erreur envoi'); }
    setSending(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" onClick={onClose}>
      <div className="lm-card w-full max-w-md p-5 space-y-4" onClick={e => e.stopPropagation()} data-testid="email-modal">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Mail size={16} className="text-[rgb(var(--lm-gold))]" />
            <h2 className="text-sm font-medium text-[rgb(var(--lm-text))]">Envoyer le menu par email</h2>
          </div>
          <button onClick={onClose} className="text-[rgb(var(--lm-text-muted))] hover:text-[rgb(var(--lm-text))]"><X size={16} /></button>
        </div>

        {/* Context: what menu we're sending */}
        <div className="p-3 rounded-lg bg-[rgb(var(--lm-surface))] border border-[rgb(var(--lm-border))]/20">
          <p className="text-[10px] font-bold text-[rgb(var(--lm-gold))] uppercase tracking-wider mb-1">{menu.titre || 'Menu du Jour'}</p>
          <div className="text-[10px] text-[rgb(var(--lm-text-muted))] space-y-0.5">
            {menu.entrees?.length > 0 && <p>Entrees: {menu.entrees.map(e => e.nom).join(', ')}</p>}
            {menu.plats?.length > 0 && <p>Plats: {menu.plats.map(p => p.nom).join(', ')}</p>}
            {menu.desserts?.length > 0 && <p>Desserts: {menu.desserts.map(d => d.nom).join(', ')}</p>}
          </div>
        </div>

        <div>
          <label className="text-[9px] text-[rgb(var(--lm-text-muted))] uppercase tracking-wider mb-1 block">Destinataires (separes par des virgules)</label>
          <textarea value={to} onChange={e => setTo(e.target.value)} rows={2}
            placeholder="email1@exemple.com, email2@exemple.com"
            className="w-full bg-[rgb(var(--lm-surface))] border border-[rgb(var(--lm-border))] rounded-md px-3 py-2 text-xs text-[rgb(var(--lm-text))] resize-none focus:outline-none focus:ring-1 focus:ring-[rgb(var(--lm-gold))]"
            data-testid="email-to" autoFocus />
        </div>

        <div>
          <div className="flex items-center justify-between mb-1">
            <label className="text-[9px] text-[rgb(var(--lm-text-muted))] uppercase tracking-wider">Message</label>
          </div>
          <div className="flex gap-2 mb-2">
            <input value={iaInstruction} onChange={e => setIaInstruction(e.target.value)}
              placeholder="Vos indications pour l'IA (ex: ton festif, parle du poisson frais, fete des meres...)"
              className="flex-1 bg-purple-50 border border-purple-200 rounded-md px-3 py-1.5 text-xs text-purple-800 placeholder:text-purple-300 focus:outline-none focus:ring-1 focus:ring-purple-400"
              data-testid="ia-instruction-input" />
            <button onClick={generateMessage} disabled={generatingIA}
              className="flex items-center gap-1 px-3 py-1.5 rounded-md bg-purple-600 text-white text-[10px] font-medium hover:bg-purple-700 transition-all shrink-0"
              data-testid="ia-generate-btn">
              {generatingIA ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />}
              {generatingIA ? 'IA...' : 'Generer'}
            </button>
          </div>
          <textarea value={message} onChange={e => setMessage(e.target.value)} rows={4}
            placeholder="Donnez vos indications ci-dessus et cliquez 'Generer', ou ecrivez directement..."
            className="w-full bg-[rgb(var(--lm-surface))] border border-[rgb(var(--lm-border))] rounded-md px-3 py-2 text-xs text-[rgb(var(--lm-text))] resize-none focus:outline-none focus:ring-1 focus:ring-[rgb(var(--lm-gold))]"
            style={{ fontFamily: 'Georgia, serif', lineHeight: '1.7' }}
            data-testid="email-message" />
        </div>

        <div className="p-2.5 rounded-lg bg-[rgb(var(--lm-gold)/0.06)] border border-[rgb(var(--lm-gold)/0.15)]">
          <p className="text-[10px] text-[rgb(var(--lm-gold))]">Le menu sera inclus dans l'email avec le PDF en piece jointe et votre signature.</p>
        </div>

        <div className="flex gap-2">
          <Button variant="ghost" onClick={onClose} className="flex-1 text-xs h-9">Annuler</Button>
          <Button onClick={sendEmail} disabled={sending} className="flex-1 bg-[rgb(var(--lm-gold))] text-[rgb(var(--lm-bg))] hover:opacity-90 text-xs h-9" data-testid="send-email-btn">
            {sending ? <Loader2 size={14} className="animate-spin mr-1" /> : <Mail size={13} className="mr-1.5" />}
            Envoyer
          </Button>
        </div>
      </div>
    </div>
  );
};

export default MenuDuJour;

/* ── Menu Sent History Modal ── */
const MenuSentHistoryModal = ({ onClose }) => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/menu-du-jour/sent?limit=100');
      setLogs(Array.isArray(r.data) ? r.data : []);
    } catch {
      toast.error('Erreur chargement historique');
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const fmtDate = (iso) => {
    if (!iso) return '—';
    try { return new Date(iso).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' }); } catch { return iso; }
  };

  const totalSent = logs.length;
  const totalOpened = logs.filter(l => (l.open_count || 0) > 0).length;
  const openRate = totalSent ? Math.round(100 * totalOpened / totalSent) : 0;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 overflow-y-auto" onClick={onClose}>
      <div className="bg-[rgb(var(--lm-card))] border border-[rgb(var(--lm-border))] rounded-xl p-5 max-w-3xl w-full my-8" onClick={e => e.stopPropagation()} data-testid="menu-sent-history-modal">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-base font-bold text-[rgb(var(--lm-text))] flex items-center gap-2">
            <History size={16} className="text-emerald-400" /> Envois & Ouvertures · Menu du Jour
          </h3>
          <button onClick={onClose} className="text-[rgb(var(--lm-text-muted))] hover:text-[rgb(var(--lm-text))]" data-testid="close-menu-history"><X size={18} /></button>
        </div>

        {/* KPIs */}
        <div className="grid grid-cols-3 gap-2 mb-4">
          <div className="bg-[rgb(var(--lm-bg))] rounded-lg p-3 border border-[rgb(var(--lm-border))]/30">
            <p className="text-[9px] uppercase tracking-wider text-[rgb(var(--lm-text-muted))] font-bold">Envoyés</p>
            <p className="text-xl font-mono font-bold text-[rgb(var(--lm-text))]">{totalSent}</p>
          </div>
          <div className="bg-[rgb(var(--lm-bg))] rounded-lg p-3 border border-[rgb(var(--lm-border))]/30">
            <p className="text-[9px] uppercase tracking-wider text-[rgb(var(--lm-text-muted))] font-bold">Ouverts</p>
            <p className="text-xl font-mono font-bold text-emerald-400">{totalOpened}</p>
          </div>
          <div className="bg-[rgb(var(--lm-bg))] rounded-lg p-3 border border-[rgb(var(--lm-border))]/30">
            <p className="text-[9px] uppercase tracking-wider text-[rgb(var(--lm-text-muted))] font-bold">Taux d'ouverture</p>
            <p className="text-xl font-mono font-bold text-[rgb(var(--lm-gold))]">{openRate}%</p>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-8"><Loader2 size={20} className="animate-spin text-[rgb(var(--lm-text-muted))]" /></div>
        ) : logs.length === 0 ? (
          <div className="py-10 text-center text-[rgb(var(--lm-text-muted))] text-sm">Aucun envoi pour l'instant</div>
        ) : (
          <div className="bg-[rgb(var(--lm-bg))] rounded-lg border border-[rgb(var(--lm-border))]/30 divide-y divide-[rgb(var(--lm-border))]/20 max-h-[55vh] overflow-y-auto">
            {logs.map((l, i) => {
              const opened = (l.open_count || 0) > 0;
              return (
                <div key={l.id || i} className="px-3 py-2.5 flex items-center justify-between gap-2 hover:bg-[rgb(var(--lm-card))]/40" data-testid={`menu-log-${i}`}>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      {opened ? (
                        <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-400 font-bold flex items-center gap-1">
                          <Eye size={10} /> Ouvert ×{l.open_count}
                        </span>
                      ) : (
                        <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-[rgb(var(--lm-border))]/30 text-[rgb(var(--lm-text-muted))] font-bold">Non ouvert</span>
                      )}
                      <p className="text-sm font-bold text-[rgb(var(--lm-text))] truncate">{l.to}</p>
                    </div>
                    <p className="text-[10px] text-[rgb(var(--lm-text-muted))] mt-0.5 truncate">
                      {l.subject}
                      {l.menu_titre ? ` · ${l.menu_titre}` : ''}
                      {l.nb_recipients > 1 ? ` · ${l.nb_recipients} destinataires` : ''}
                    </p>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="text-[10px] text-[rgb(var(--lm-text-muted))]">Envoyé</p>
                    <p className="text-[11px] font-mono text-[rgb(var(--lm-text))]">{fmtDate(l.sent_at || l.date)}</p>
                    {opened && (
                      <p className="text-[10px] font-mono text-emerald-400 mt-0.5">
                        1er: {fmtDate(l.first_opened_at)}
                      </p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <div className="mt-3 flex items-center justify-between">
          <p className="text-[10px] text-[rgb(var(--lm-text-muted))]">
            Le tracking pixel détecte l'ouverture du mail HTML (certains clients bloquent les images).
          </p>
          <button onClick={load} className="text-[11px] text-emerald-400 font-bold hover:underline" data-testid="refresh-menu-history">Rafraîchir</button>
        </div>
      </div>
    </div>
  );
};

/* ── Programmer Modal ── */
const ProgrammerModal = ({ currentMenu, onClose }) => {
  const [programmes, setProgrammes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [publishDate, setPublishDate] = useState('');
  const [dateFin, setDateFin] = useState('');
  const [titre, setTitre] = useState('Menu de la Semaine');

  const load = useCallback(() => {
    api.get('/menu-du-jour/programmes').then(r => setProgrammes(r.data || [])).catch(() => {}).finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const createFromCurrent = async () => {
    if (!publishDate) { toast.error('Date de publication requise'); return; }
    setCreating(true);
    try {
      await api.post('/menu-du-jour/programmes', {
        titre,
        publish_date: publishDate,
        date_fin: dateFin,
        entrees: currentMenu?.entrees || [],
        plats: currentMenu?.plats || [],
        desserts: currentMenu?.desserts || [],
        formules: currentMenu?.formules || [],
      });
      toast.success('Menu programme !');
      setPublishDate('');
      setDateFin('');
      load();
    } catch { toast.error('Erreur'); }
    setCreating(false);
  };

  const publishNow = async (id) => {
    try {
      await api.post(`/menu-du-jour/programmes/${id}/publish`);
      toast.success('Menu publie sur le site !');
      load();
    } catch { toast.error('Erreur'); }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/menu-du-jour/programmes/${id}`);
      toast.success('Supprime');
      load();
    } catch { toast.error('Erreur'); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" onClick={onClose}>
      <div className="lm-card w-full max-w-lg p-5 space-y-4 max-h-[85vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="programmer-modal">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Clock size={16} className="text-[rgb(var(--lm-gold))]" />
            <h2 className="text-sm font-medium text-[rgb(var(--lm-text))]">Programmer un menu</h2>
          </div>
          <button onClick={onClose} className="text-[rgb(var(--lm-text-muted))] hover:text-[rgb(var(--lm-text))]"><X size={16} /></button>
        </div>

        {/* Create from current */}
        <div className="p-3 rounded-lg bg-[rgb(var(--lm-surface))] border border-[rgb(var(--lm-border))] space-y-2">
          <p className="text-[10px] text-[rgb(var(--lm-text-muted))] uppercase tracking-wider">Programmer le menu actuel</p>
          <Input value={titre} onChange={e => setTitre(e.target.value)} placeholder="Titre"
            className="bg-[rgb(var(--lm-bg))] border-[rgb(var(--lm-border))] text-xs h-8" data-testid="prog-titre" />
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[9px] text-[rgb(var(--lm-text-muted))] uppercase block mb-0.5">Publication</label>
              <Input type="date" value={publishDate} onChange={e => setPublishDate(e.target.value)}
                className="bg-[rgb(var(--lm-bg))] border-[rgb(var(--lm-border))] text-xs h-8" data-testid="prog-date" />
            </div>
            <div>
              <label className="text-[9px] text-[rgb(var(--lm-text-muted))] uppercase block mb-0.5">Fin (optionnel)</label>
              <Input type="date" value={dateFin} onChange={e => setDateFin(e.target.value)}
                className="bg-[rgb(var(--lm-bg))] border-[rgb(var(--lm-border))] text-xs h-8" data-testid="prog-date-fin" />
            </div>
          </div>
          <div className="p-2 rounded-md bg-[rgb(var(--lm-gold)/0.06)] border border-[rgb(var(--lm-gold)/0.15)]">
            <p className="text-[9px] text-[rgb(var(--lm-gold))]">
              Le menu actuel ({currentMenu?.entrees?.length || 0} entrees, {currentMenu?.plats?.length || 0} plats, {currentMenu?.desserts?.length || 0} desserts) sera copie et publie automatiquement le jour choisi.
            </p>
          </div>
          <Button onClick={createFromCurrent} disabled={creating} size="sm" className="w-full bg-[rgb(var(--lm-gold))] text-[rgb(var(--lm-bg))] hover:opacity-90 text-xs h-8" data-testid="prog-create">
            {creating ? <Loader2 size={12} className="animate-spin mr-1" /> : <Clock size={12} className="mr-1" />}
            Programmer
          </Button>
        </div>

        {/* List */}
        <div>
          <p className="text-[10px] text-[rgb(var(--lm-text-muted))] uppercase tracking-wider mb-2">Menus programmes</p>
          {loading ? (
            <div className="text-center py-4"><Loader2 size={16} className="animate-spin text-[rgb(var(--lm-text-muted))] mx-auto" /></div>
          ) : programmes.length === 0 ? (
            <p className="text-xs text-[rgb(var(--lm-text-muted))] text-center py-4">Aucun menu programme</p>
          ) : (
            <div className="space-y-1.5">
              {programmes.map(p => (
                <div key={p.id} className="flex items-center gap-2 p-2.5 rounded-lg bg-[rgb(var(--lm-surface))] border border-[rgb(var(--lm-border))]" data-testid={`prog-${p.id}`}>
                  <div className={`w-2 h-8 rounded-full shrink-0 ${p.published ? 'bg-green-400' : 'bg-[rgb(var(--lm-gold))]'}`} />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium text-[rgb(var(--lm-text))] truncate">{p.titre}</p>
                    <p className="text-[9px] text-[rgb(var(--lm-text-muted))]">
                      {p.published ? 'Publie' : `Publication: ${new Date(p.publish_date + 'T00:00:00').toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })}`}
                      {' — '}{p.entrees?.length || 0}E {p.plats?.length || 0}P {p.desserts?.length || 0}D
                    </p>
                  </div>
                  {!p.published && (
                    <button onClick={() => publishNow(p.id)} className="p-1.5 rounded-md bg-green-500/10 text-green-500 hover:bg-green-500/20 transition" title="Publier maintenant" data-testid={`publish-${p.id}`}>
                      <Check size={12} />
                    </button>
                  )}
                  <button onClick={() => remove(p.id)} className="p-1.5 rounded-md hover:bg-red-500/10 text-red-400 transition" data-testid={`remove-prog-${p.id}`}>
                    <Trash2 size={12} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

